/**
 * Hook for password-based authentication with Clerk.
 *
 * Handles sign-in with email and password, including session activation.
 * When Clerk's Client Trust feature triggers a step-up verification on a
 * new device, this hook surfaces a `needs_client_trust` result so the
 * caller can collect a code and complete the flow via `verifyClientTrust`.
 *
 * Aliases: password login hook, password authentication hook
 */

import { useClerk } from "@clerk/tanstack-react-start";
import type { SignInResource } from "@clerk/types";
import { useCallback, useRef, useState } from "react";

import { getClerkErrorMessage } from "~/lib/clerk-errors";

import {
  findClientTrustFactor,
  prepareClientTrustFactor,
} from "./client-trust";

export type ClientTrustChannel = {
  readonly strategy: "email_code" | "phone_code";
  readonly safeIdentifier: string;
};

export type SecondFactorStrategy = "totp" | "backup_code";

export type PasswordLoginOutcome =
  | { readonly status: "complete" }
  | {
      readonly status: "needs_client_trust";
      readonly channel: ClientTrustChannel;
    }
  | {
      readonly status: "needs_second_factor";
      readonly strategies: readonly SecondFactorStrategy[];
    };

export interface UsePasswordLoginResult {
  readonly login: (
    email: string,
    password: string,
  ) => Promise<PasswordLoginOutcome>;
  readonly verifyClientTrust: (code: string) => Promise<void>;
  readonly resendClientTrustCode: () => Promise<void>;
  readonly verifySecondFactor: (
    code: string,
    strategy: SecondFactorStrategy,
  ) => Promise<void>;
  readonly clientTrustChannel: ClientTrustChannel | null;
  readonly secondFactorStrategies: readonly SecondFactorStrategy[];
  readonly isLoading: boolean;
  readonly error: string;
  readonly clearError: () => void;
}

const findSecondFactorStrategies = (
  signIn: SignInResource,
): SecondFactorStrategy[] => {
  const strategies = (signIn.supportedSecondFactors ?? [])
    .map((factor) => factor?.strategy)
    .filter(
      (strategy): strategy is SecondFactorStrategy =>
        strategy === "totp" || strategy === "backup_code",
    );
  // De-duplicate while keeping totp first so it is the default prompt.
  return Array.from(new Set(strategies)).sort((a, b) =>
    a === "totp" ? -1 : b === "totp" ? 1 : 0,
  );
};

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const usePasswordLogin = (): UsePasswordLoginResult => {
  const clerk = useClerk();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientTrustChannel, setClientTrustChannel] =
    useState<ClientTrustChannel | null>(null);
  const [secondFactorStrategies, setSecondFactorStrategies] = useState<
    readonly SecondFactorStrategy[]
  >([]);
  const signInRef = useRef<SignInResource | null>(null);

  const clearError = useCallback(() => {
    setError("");
  }, []);

  const finalizeSession = useCallback(
    async (signIn: SignInResource): Promise<void> => {
      if (!signIn.createdSessionId) {
        throw new Error("Missing session identifier");
      }
      if (!clerk) {
        throw new Error("Clerk client not available");
      }
      await clerk.setActive({ session: signIn.createdSessionId });
    },
    [clerk],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<PasswordLoginOutcome> => {
      setIsLoading(true);
      setError("");
      setClientTrustChannel(null);
      setSecondFactorStrategies([]);
      signInRef.current = null;

      try {
        if (!clerk?.client) {
          throw new Error("Clerk client not available");
        }

        const normalizedEmail = normalizeEmail(email);

        const signIn = await clerk.client.signIn.create({
          identifier: normalizedEmail,
          password,
        });
        signInRef.current = signIn;

        // `needs_client_trust` was added after our installed @clerk/types
        // version. Compare as a string so the runtime value matches even
        // when the static `SignInStatus` union doesn't list it yet.
        if ((signIn.status as string) === "needs_client_trust") {
          const factor = findClientTrustFactor(signIn);
          if (!factor) {
            throw new Error(
              "Client trust verification is required, but no email or phone factor is available.",
            );
          }
          const prepared = await prepareClientTrustFactor(signIn, factor);
          signInRef.current = prepared;
          const channel: ClientTrustChannel = {
            strategy: factor.strategy,
            safeIdentifier: factor.safeIdentifier,
          };
          setClientTrustChannel(channel);
          return { status: "needs_client_trust", channel };
        }

        // MFA-enabled accounts return `needs_second_factor`. Clerk surfaces
        // MFA in preference to Client Trust, so this is a distinct branch:
        // collect a TOTP or backup code and complete via `attemptSecondFactor`.
        if (signIn.status === "needs_second_factor") {
          const strategies = findSecondFactorStrategies(signIn);
          if (strategies.length === 0) {
            throw new Error(
              "Two-factor authentication is required, but no supported method is available.",
            );
          }
          setSecondFactorStrategies(strategies);
          return { status: "needs_second_factor", strategies };
        }

        if (signIn.status !== "complete") {
          throw new Error("Sign-in incomplete");
        }

        await finalizeSession(signIn);
        return { status: "complete" };
      } catch (err: unknown) {
        const message = getClerkErrorMessage(err, "Failed to sign in");
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [clerk, finalizeSession],
  );

  const verifyClientTrust = useCallback(
    async (code: string): Promise<void> => {
      const signIn = signInRef.current;
      if (!signIn || !clientTrustChannel) {
        throw new Error("No client trust verification in progress");
      }

      setIsLoading(true);
      setError("");

      try {
        const verified = await signIn.attemptSecondFactor({
          strategy: clientTrustChannel.strategy,
          code,
        });
        signInRef.current = verified;

        if (verified.status !== "complete") {
          throw new Error("Verification did not complete the sign-in.");
        }

        await finalizeSession(verified);
        setClientTrustChannel(null);
        signInRef.current = null;
      } catch (err: unknown) {
        const message = getClerkErrorMessage(
          err,
          "Invalid or expired verification code.",
        );
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [clientTrustChannel, finalizeSession],
  );

  const verifySecondFactor = useCallback(
    async (code: string, strategy: SecondFactorStrategy): Promise<void> => {
      const signIn = signInRef.current;
      if (!signIn) {
        throw new Error("No sign-in attempt in progress");
      }

      setIsLoading(true);
      setError("");

      try {
        const verified =
          strategy === "backup_code"
            ? await signIn.attemptSecondFactor({
                strategy: "backup_code",
                code,
              })
            : await signIn.attemptSecondFactor({ strategy: "totp", code });
        signInRef.current = verified;

        if (verified.status !== "complete") {
          throw new Error("Verification did not complete the sign-in.");
        }

        await finalizeSession(verified);
        setSecondFactorStrategies([]);
        signInRef.current = null;
      } catch (err: unknown) {
        const message = getClerkErrorMessage(
          err,
          "Invalid or expired verification code.",
        );
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [finalizeSession],
  );

  const resendClientTrustCode = useCallback(async (): Promise<void> => {
    const signIn = signInRef.current;
    if (!signIn || !clientTrustChannel) {
      throw new Error("No client trust verification in progress");
    }
    const factor = findClientTrustFactor(signIn);
    if (!factor || factor.strategy !== clientTrustChannel.strategy) {
      throw new Error(
        "Client trust verification factor is no longer available.",
      );
    }
    try {
      const prepared = await prepareClientTrustFactor(signIn, factor);
      signInRef.current = prepared;
    } catch (err: unknown) {
      const message = getClerkErrorMessage(
        err,
        "Failed to resend the verification code.",
      );
      setError(message);
      throw err;
    }
  }, [clientTrustChannel]);

  return {
    login,
    verifyClientTrust,
    resendClientTrustCode,
    verifySecondFactor,
    clientTrustChannel,
    secondFactorStrategies,
    isLoading,
    error,
    clearError,
  };
};
